import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { CreditCard, ShieldCheck } from "lucide-react";
import { Container } from "@/components/ui-kit";
import { useCustomerAuth } from "@/hooks/useCustomerAuth";
import {
  createCustomerCheckout, createCustomerOrder, fetchCheckoutQuote,
} from "@/lib/api";
import { getPlanBySlug } from "@/data/content";

const money = (minor, currency) => new Intl.NumberFormat(currency === "PHP" ? "en-PH" : "en-IE", {
  style: "currency", currency,
}).format((minor || 0) / 100);
const TEST_PLAN_DISPLAY = {
  "test-handover": { slug: "test-handover", name: "Test Handover" },
  "test-basic": { slug: "test-basic", name: "Test Basic" },
  "test-responsibility": { slug: "test-responsibility", name: "Test Responsibility" },
  "test-priority": { slug: "test-priority", name: "Test Priority" },
};

export default function PaymentDetails() {
  const { token, customer, checking, isLoggedIn } = useCustomerAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  const planSlug = params.get("plan") || "website-ownership";
  const market = params.get("market") === "eu" ? "eu" : "ph";
  const plan = getPlanBySlug(planSlug) || TEST_PLAN_DISPLAY[planSlug];
  const [code, setCode] = useState("");
  const [promo, setPromo] = useState("");
  const [assistedSetup, setAssistedSetup] = useState(false);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const intentPath = `${location.pathname}${location.search}`;
  const backendPlanCode = plan?.slug || planSlug;

  useEffect(() => {
    window.sessionStorage.setItem("mysitecare-checkout-intent", intentPath);
  }, [intentPath]);

  const payload = useMemo(() => ({
    plan_code: backendPlanCode,
    market,
    website_access_code: code,
    promo_code: promo || null,
    assisted_setup: assistedSetup,
  }), [backendPlanCode, market, code, promo, assistedSetup]);

  if (checking) return null;
  if (!isLoggedIn) {
    return <Navigate to={`/account/login?return_to=${encodeURIComponent(intentPath)}`} replace />;
  }
  if (!plan) return <Navigate to={market === "ph" ? "/ph/plans" : "/plans"} replace />;

  const validate = async () => {
    setBusy(true); setError(""); setQuote(null);
    try {
      setQuote(await fetchCheckoutQuote(token, payload));
    } catch (err) {
      setError(err.response?.data?.detail || "Could not validate these payment details.");
    } finally { setBusy(false); }
  };

  const createOrder = async () => {
    setBusy(true); setError("");
    try {
      const freshQuote = await fetchCheckoutQuote(token, payload);
      setQuote(freshQuote);
      const order = await createCustomerOrder(token, payload);
      if (freshQuote.billing_type === "RECURRING" && !freshQuote.recurring_payment_available) {
        throw new Error("Automatic recurring billing is not enabled yet. Please use Inquire so MySiteCare can arrange activation.");
      }
      const checkout = await createCustomerCheckout(token, order.id);
      window.location.assign(checkout.checkout_url);
      return null;
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Could not start payment.");
      return null;
    } finally { setBusy(false); }
  };


  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12" data-testid="payment-details-page">
      <Container className="max-w-5xl">
        <div className="grid gap-8 lg:grid-cols-[1.2fr_.8fr]">
          <section className="rounded-3xl border border-[#E2E8F0] bg-white p-7 md:p-9 shadow-sm">
            <div className="flex items-center gap-3"><ShieldCheck className="text-[#10B981]" /><div><p className="text-xs font-bold uppercase tracking-widest text-[#10B981]">Secure checkout</p><h1 className="text-3xl font-bold font-display text-[#0F172A]">Payment details</h1></div></div>
            <div className="mt-7 rounded-2xl bg-slate-50 p-5"><p className="text-xs uppercase tracking-wide text-slate-500">Customer</p><p className="mt-1 font-semibold">{customer?.display_name}</p><p className="text-sm text-slate-600">{customer?.email}</p></div>

            <div className="mt-6">
              <label htmlFor="website-code" className="text-sm font-semibold text-[#0F172A]">Website Access Code <span className="text-red-600">Required</span></label>
              <p className="mt-1 text-xs text-slate-500">This identifies the exact website being purchased. It is separate from a discount code.</p>
              <div className="mt-2 flex gap-2"><input id="website-code" data-testid="payment-website-code-input" value={code} onChange={(e) => { setCode(e.target.value); setQuote(null); }} placeholder="MSC-XXXX-XXXX-XXXX" className="min-w-0 flex-1 rounded-xl border border-slate-200 px-4 py-3 font-mono" />
              <button data-testid="payment-validate-code-button" type="button" onClick={validate} disabled={busy || !code.trim()} className="rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white disabled:opacity-50">Validate</button></div>
              {quote?.website && <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm"><strong>Website confirmed:</strong> {quote.website.business_name}</div>}
            </div>

            {plan.slug === "website-ownership" && <label className="mt-6 flex items-start gap-3 rounded-xl border border-slate-200 p-4"><input type="checkbox" checked={assistedSetup} onChange={(e) => { setAssistedSetup(e.target.checked); setQuote(null); }} className="mt-1" /><span><strong>Assisted Setup &amp; Launch</strong><span className="block text-sm text-slate-500">Optional one-time deployment and launch assistance.</span></span></label>}
            <div className="mt-6"><label htmlFor="promo-code" className="text-sm font-semibold">Promo / Discount Code <span className="font-normal text-slate-500">Optional</span></label><input id="promo-code" data-testid="payment-promo-code-input" value={promo} onChange={(e) => { setPromo(e.target.value); setQuote(null); }} className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3" /></div>
            {error && <p className="mt-4 text-sm text-red-600" role="alert">{error}</p>}
          </section>

          <aside className="h-fit rounded-3xl bg-[#0F172A] p-7 text-white">
            <h2 className="text-xl font-bold font-display">Order summary</h2><p className="mt-3 text-slate-300">{plan.name}</p><p className="text-sm text-slate-400">{market === "ph" ? "Philippines" : "EU / International"}</p>
            {quote ? <div className="mt-6 space-y-3 text-sm"><div className="flex justify-between"><span>Base price</span><span>{money(quote.base_amount_minor, quote.currency)}</span></div>{quote.assisted_setup && <div className="flex justify-between"><span>Assisted Setup</span><span>{money(quote.assisted_setup_amount_minor, quote.currency)}</span></div>}<div className="flex justify-between"><span>Discount</span><span>-{money(quote.discount_minor, quote.currency)}</span></div><div className="border-t border-white/20 pt-4 flex justify-between text-lg font-bold"><span>Total</span><span>{money(quote.total_minor, quote.currency)}</span></div></div> : <p className="mt-6 text-sm text-slate-400">Validate your Website Access Code to receive the server-calculated total.</p>}
            <button data-testid="payment-continue-paymongo-button" type="button" onClick={createOrder} disabled={busy || !quote} className="mt-7 w-full rounded-xl bg-[#10B981] px-5 py-3.5 font-semibold disabled:opacity-50"><CreditCard className="mr-2 inline" size={17} />{busy ? "Please wait…" : "Continue to PayMongo"}</button>
            <p className="mt-5 text-xs text-slate-400">Prices and discounts are calculated by MySiteCare’s server. Reaching a success URL never grants access by itself.</p>
            <Link to={market === "ph" ? "/ph/contact" : "/contact"} className="mt-4 block text-center text-sm text-emerald-300 hover:underline">Need help? Inquire first</Link>
          </aside>
        </div>
      </Container>
    </div>
  );
}
